import { h } from "preact";
import { useState, useEffect, useRef, useCallback } from "preact/hooks";
import { useLocationFormStorage } from "./locationFormContext";
import { LocationSuggestionsTab } from "./locationOverlayTabs/locationSuggestionsTab";
import { AddressSuggestionsTab } from "./locationOverlayTabs/addressSuggestionsTab";
import { RentalSuggestionsTab } from "./locationOverlayTabs/rentalSuggestionsTab";

const getMapboxToken = () => {
  return typeof window !== "undefined" ? window.mapbox_key : null;
};

export function LocationOverlay() {
  const {
    location,
    geoItem,
    setGeoItem,
    setLocation,
    setShowAutocompleteOverlay,
  } = useLocationFormStorage();
  const [activeTab, setActiveTab] = useState("locations");
  const [geoResults, setGeoResults] = useState([]);
  const [addressResults, setAddressResults] = useState([]);
  const [rentalResults, setRentalResults] = useState([]);
  const geocodeAbortRef = useRef(null);

  const fetchGeocoding = useCallback(async (query, signal) => {
    const token = getMapboxToken();
    if (!token || !query || query.length < 3) {
      return [];
    }
    try {
      const currentLang = typeof lang !== "undefined" ? lang : "de";
      const response = await fetch(
        "https://api.mapbox.com/search/geocode/v6/forward?" +
          new URLSearchParams({
            q: query,
            access_token: token,
            language: currentLang,
          }),
        { signal }
      );
      const results = await response.json();
      return results.features || [];
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Mapbox geocoding error:", error);
      }
      return [];
    }
  }, []);

  useEffect(() => {
    if (!location || location.length < 3) {
      setGeoResults([]);
      setRentalResults([]);
      setAddressResults([]);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;
    const currentLang = typeof lang !== "undefined" ? lang : "de";
    const url = window.Routing.generate("_autocomplete." + currentLang);

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(
          url + "?" + new URLSearchParams({ q: location }),
          { signal }
        );
        const results = await response.json();
        const locations = results.locations || [];
        const rentals = results.rentals || [];

        setGeoResults(locations);
        setRentalResults(rentals);

        if (locations.length > 0) {
          // Places found: do NOT trigger geocoder
          setAddressResults([]);
          setActiveTab("locations");
        } else {
          // No places found: trigger geocoder
          const features = await fetchGeocoding(location, signal);
          setAddressResults(features);
          if (rentals.length > 0) {
            setActiveTab("rentals");
          } else {
            setActiveTab("address");
          }
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          setGeoResults([]);
          setRentalResults([]);
          setAddressResults([]);
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [location, fetchGeocoding]);

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    if (
      tab === "address" &&
      addressResults.length === 0 &&
      location &&
      location.length >= 3
    ) {
      if (geocodeAbortRef.current) {
        geocodeAbortRef.current.abort();
      }
      geocodeAbortRef.current = new AbortController();
      const features = await fetchGeocoding(
        location,
        geocodeAbortRef.current.signal
      );
      setAddressResults(features);
    }
  };

  const setAddressItem = (address) => {
    const fullAddress =
      address.properties?.full_address || address.properties?.name || "";
    const coordinates = address.geometry?.coordinates || [];
    const currentLang = typeof lang !== "undefined" ? lang : "de";
    setLocation(fullAddress);
    setGeoItem({
      type: "address",
      url: window.Routing.generate("_search_address." + currentLang, {
        locationId: coordinates.join("-"),
        locationSlug:
          typeof window.slugify === "function"
            ? window.slugify(fullAddress)
            : encodeURIComponent(fullAddress),
      }),
    });
    setShowAutocompleteOverlay(false);
  };

  const setLocationItem = (locationResult) => {
    setLocation(locationResult.value);
    setGeoItem(locationResult);
    setShowAutocompleteOverlay(false);
  };

  const closeOverlay = () => {
    if (!geoItem) {
      setLocation("");
    }
    setShowAutocompleteOverlay(false);
  };

  return (
    <div id="location-overlay" className="col-span-1 md:col-span-1">
      <div className="absolute top-0 left-0 w-full rounded-lg bg-white p-4 shadow-lg lg:p-6 z-10">
        <button
          type="button"
          className="absolute top-4 right-4 z-50 text-brandcopy-700 hover:text-brandcopy-900"
          aria-label="Formular schließen"
          onClick={() => closeOverlay()}
        >
          <svg
            aria-hidden="true"
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="grid grid-cols-3 gap-4 mt-[70px]">
          <button
            onClick={() => handleTabChange("locations")}
            type="button"
            className={`text-xs md:text-sm lg:text-basetab-button flex w-full items-start justify-center rounded-t-lg p-4 transition text-brandcopy-900 hover:bg-brand-100 ${activeTab === "locations" ? "bg-brand-100" : ""}`}
          >
            {global.Translator.trans("locationform.locations", {}, "js")}
          </button>
          <button
            onClick={() => handleTabChange("address")}
            type="button"
            className={`text-xs md:text-sm lg:text-basetab-button flex w-full items-start justify-center rounded-t-lg p-4 transition text-brandcopy-900 hover:bg-brand-100 ${activeTab === "address" ? "bg-brand-100" : ""}`}
          >
            {global.Translator.trans("locationform.address", {}, "js")}
          </button>
          <button
            onClick={() => handleTabChange("rentals")}
            type="button"
            className={`text-xs md:text-sm lg:text-basetab-button flex w-full items-start justify-center rounded-t-lg p-4 transition text-brandcopy-900 hover:bg-brand-100 ${activeTab === "rentals" ? "bg-brand-100" : ""}`}
          >
            {global.Translator.trans("locationform.rentals", {}, "js")}
          </button>
        </div>
        <div className="max-h-[300px]">
          {activeTab === "locations" && (
            <LocationSuggestionsTab
              geoResults={geoResults}
              onSelect={setLocationItem}
            />
          )}
          {activeTab === "address" && (
            <AddressSuggestionsTab
              addressResults={addressResults}
              onSelect={setAddressItem}
            />
          )}
          {activeTab === "rentals" && (
            <RentalSuggestionsTab rentalResults={rentalResults} />
          )}
        </div>
      </div>
    </div>
  );
}
